import React, { useEffect, useState } from 'react';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { IQuestion } from '@/components/staffer/Grading/types';
import { applyGradeByStudentFilters } from '@/components/staffer/Grading/utils';
import I18n from 'i18n-js';
import ModelQuestionsContainer from '@/components/staffer/Grading/ModelQuestionsContainer';
import QuestionCard from './QuestionCards/QuestionCard';

const CardContainer = () => {
  const {
    assignment,
    isTeacher,
    questionAnswers,
  } = useGradingStore();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { questions, grouped_questions } = assignment;
  const [filteredQuestions, setFilteredQuestions] = useState<IQuestion[]>(questions);
  const [groupIsExpanded, setGroupIsExpanded] = useState({});
  const { filters } = useGradingStore.getState();
  const { autoGradedFilter, questionsFilter, statusFilter } = filters;

  useEffect(() => {
    if (!questions) return;

    setFilteredQuestions(
      isTeacher ? applyGradeByStudentFilters({
        autoGradedFilter,
        questionAnswers,
        questions,
        questionsFilter,
        statusFilter,
      }) : questions,
    );
  }, [autoGradedFilter, questionAnswers, questions, questionsFilter, statusFilter]);

  if (!filteredQuestions || filteredQuestions.length < 1) {
    return (
      <p className="tw-mt-16 tw-w-full tw-font-semibold tw-text-center">
        {I18n.t('no_questions_to_grade')}
      </p>
    );
  }

  if (filteredQuestions.some(q => ['Notebook', 'ReadingGame'].includes(q.question_group.exercise_type))) {
    const toggleExpanded = (keys: string[]) => {
      const groupsToToggle = {};
      keys.forEach((key: string) => { groupsToToggle[key] = !groupIsExpanded[key]; });
      setGroupIsExpanded({ ...groupIsExpanded, ...groupsToToggle });
    };

    const allExpanded = () => {
      if (Object.values(groupIsExpanded).length === 0) return false;

      return Object.values(groupIsExpanded).every(value => value === true);
    };

    const toggleAll = () => {
      const groupsToToggle = {};

      if (allExpanded()) {
        Object.keys(grouped_questions).forEach((key: string) => { groupsToToggle[key] = false; });
      } else {
        Object.keys(grouped_questions).forEach((key: string) => { groupsToToggle[key] = true; });
      }

      setGroupIsExpanded(groupsToToggle);
    };

    return (
      <>
        <div className="tw-flex tw-mt-5">
          <button
            className="btn btn--outline-purple tw-ml-auto"
            type="button"
            onClick={toggleAll}
          >
            <i className={`tw-mr-2 fa ${allExpanded() ? 'fa-compress' : 'fa-expand'}`} />
            {allExpanded() ? 'Collapse' : 'Expand'} All Sections
          </button>
        </div>
        {
          Object.keys(grouped_questions).map((key) => {
            const modelTypeAndId = key.split('-');
            return (
              <ModelQuestionsContainer
                key={`model-questions-container-${key}`}
                isExpanded={groupIsExpanded[key]}
                modelId={modelTypeAndId[1]}
                modelType={modelTypeAndId[0]}
                questionAnswers={questionAnswers}
                questions={grouped_questions[key]}
                toggleExpanded={toggleExpanded}
              />
            );
          })
        }
      </>
    );
  }

  return (
    <>
      {filteredQuestions?.map((q) => {
        const questionAnswer = questionAnswers?.find(qa => qa.question_id === q.id);

        return (
          <QuestionCard
            key={q.id}
            question={q}
            questionAnswer={questionAnswer}
          />
        );
      })}
    </>
  );
};

export default CardContainer;
